import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChildren, signal, inject } from "@angular/core";
import { CompanyModalService } from "company/services/company-modal.service";
import { max } from "lodash";
import { IHierarchyLabelContext } from "rl-common/models/i-hierarchy-label-context";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRule } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule";
import { IAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule-set";
import { IUpdateGroupRuleSetSequenceNumberOrder } from "rl-common/services/accounting-processes/models/i-update-group-rule-set-sequence-number-order";
import { GrowlerService } from "rl-common/services/growler.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { Observable, Subject, Subscription } from "rxjs";
import { catchError, debounceTime, filter, switchMap, tap } from "rxjs/operators";
import { AccountGroupRulesComponent } from "../account-group-rules/account-group-rules.component";

/** what the Add/Edit Rule Set modal needs to know about the Sets already in this group */
export interface IRuleSetsLoaded {
	groupId: string;
	maxSequenceNumber: number;
	existingNames: string[];
}

@Component({
	selector: "rl-account-group-rule-sets",
	templateUrl: "./account-group-rule-sets.component.html",
	styleUrls: ["./account-group-rule-sets.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CdkDropList, CdkDrag, CdkDragHandle, AccountGroupRulesComponent]
})
export class AccountGroupRuleSetsComponent implements OnInit, OnDestroy {
	private readonly _accountingProcessesService = inject(AccountingProcessesService);
	private readonly _promptService = inject(PromptService);
	private readonly _growlerService = inject(GrowlerService);
	private readonly _modalService = inject(CompanyModalService);
	private readonly _injector = inject(Injector);

	@Input()
	accountGroup: IAccountingProcessAccountGroup;

	@Input()
	defaultAccounts: [string, string][] = []; //[ID, NAME]

	/** feeds the Add/Edit Rule Set modal: it needs the next sequence number and the taken names */
	@Output()
	ruleSetsLoaded = new EventEmitter<IRuleSetsLoaded>();

	// Rule Set add/edit stays with chart-of-accounts: the Set's Account dropdown needs that
	// component's account list. Rules are handled here -- see addOrEditAccountGroupRule.
	@Output()
	editRuleSet = new EventEmitter<{ group: IAccountingProcessAccountGroup, ruleSet: IAccountingProcessAccountGroupRuleSet }>();

	@ViewChildren(AccountGroupRulesComponent)
	ruleComponents: QueryList<AccountGroupRulesComponent>;

	groupId: string;
	ruleSets: IAccountingProcessAccountGroupRuleSet[] = [];

	// Shared by every rule under this Account Group: building a HierarchyLabelContext walks the whole
	// LOV, and different Sets in a group routinely target the same field. Scoped to the group rather
	// than made a service so it cannot serve a stale hierarchy after an admin edits one -- the group's
	// accordion is rebuilt on reload, which discards this with it.
	readonly hlcCache = new Map<string, IHierarchyLabelContext>();

	loadRuleSets$: Observable<IAccountingProcessAccountGroupRuleSet[]>;
	_subs: Subscription[] = [];

	// Tracks COLLAPSED ids, so a Set absent from the set renders expanded -- new Sets show their
	// rules immediately, and keying on groupRuleSetId (not index) means a reorder can't drag one
	// Set's collapse state onto its neighbour.
	private readonly _collapsed = signal(new Set<string>());

	/** drag-reorders to persist; see the pipe in ngOnInit for why they are not saved inline */
	private readonly _reorder$ = new Subject<void>();

	ngOnInit(): void {
		this.groupId = this.accountGroup.id;
		this.loadRuleSets();

		// Debounce the reorders, not the responses: piping debounceTime after the save call delays
		// the result of a request that already went out, so a flurry of drags still fires a request
		// each and whichever one happens to finish last wins -- which can persist a stale order.
		// Debouncing here coalesces the flurry into one save, and switchMap drops any in-flight save
		// so the latest order is always the one that lands. The payload is built inside the switchMap
		// so it reflects the array as of the save, not as of the drop.
		const reorderSub = this._reorder$.pipe(
			debounceTime(200),
			switchMap(() => {
				const seqNumOrder = {
					groupId: this.groupId,
					groupRuleSetIds: this.ruleSets.map(rs => rs.groupRuleSetId).join("|")
				} as IUpdateGroupRuleSetSequenceNumberOrder;
				return this._accountingProcessesService.updateGroupRuleSetSequenceNumberOrder(seqNumOrder);
			})
		).subscribe();

		this._subs.push(reorderSub);
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}

	loadRuleSets(): void {
		// sort by sequenceNumber: the displayed order has to match the evaluation order, since the
		// first Set whose rules all match is the one that wins
		this.loadRuleSets$ = this._accountingProcessesService.getAccountGroupRuleSets(this.groupId).pipe(
			tap(ruleSets => {
				this.ruleSets = [...ruleSets].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
				this.ruleSetsLoaded.emit({
					groupId: this.groupId,
					maxSequenceNumber: max(this.ruleSets.map(rs => rs.sequenceNumber)) ?? 0,
					existingNames: this.ruleSets.map(rs => rs.name)
				});
			})
		);

		this._subs.push(this.loadRuleSets$.subscribe());
	}

	/** called after a rule is added or edited, to refresh just the Set that changed */
	reloadRulesForSet(groupRuleSetId: string): void {
		this.ruleComponents?.find(rc => rc.ruleSet?.groupRuleSetId === groupRuleSetId)?.loadRules();
	}

	// Lives here rather than on chart-of-accounts because everything it needs is local: the Set owns
	// the account now, so the modal no longer has to be handed the Chart's account list.
	addOrEditAccountGroupRule(ruleSet: IAccountingProcessAccountGroupRuleSet, accountGroupRule: IAccountingProcessAccountGroupRule = null) {
		const sub = this._modalService.buildAccountGroupRule(this.accountGroup, ruleSet, accountGroupRule, this._injector).pipe(
			filter(result => !!result),
			switchMap(result => this._accountingProcessesService.upsertAccountGroupRule(result.accountGroupRule).pipe(
				tap(id => {
					if (id) {
						const message = accountGroupRule ? `Account Group Rule successfully updated` : `Account Group Rule added to Rule Set: "${ruleSet.name}"`;
						this._growlerService.success().growl(message);
						// refresh just the Set that changed, not the whole group
						this.reloadRulesForSet(ruleSet.groupRuleSetId);
					}
				}),
				catchError(err => {
					const message = err?.error?.message ?? `Error adding/editing Account Group Rule`;
					this._growlerService.error().growl(message);
					throw err;
				})
			))
		).subscribe();

		this._subs.push(sub);
	}

	isCollapsed(groupRuleSetId: string): boolean {
		return this._collapsed().has(groupRuleSetId);
	}

	toggle(groupRuleSetId: string): void {
		// copy-on-write: mutating the Set in place would not register as a signal change
		const next = new Set(this._collapsed());
		if (next.has(groupRuleSetId)) {
			next.delete(groupRuleSetId);
		} else {
			next.add(groupRuleSetId);
		}
		this._collapsed.set(next);
	}

	getAccountName(accountId: string): string {
		return this.defaultAccounts?.find(da => da[0] === accountId)?.[1];
	}

	drop(event: CdkDragDrop<string[]>) {
		if (event.previousIndex === event.currentIndex) {
			return;
		}
		moveItemInArray(this.ruleSets, event.previousIndex, event.currentIndex);
		this._reorder$.next();
	}

	deleteRuleSet(ruleSet: IAccountingProcessAccountGroupRuleSet, deleteTemplate: TemplateRef<unknown>) {
		// the guard lives at Set level now: an associated group has to keep at least one Set, but
		// the rules within a Set are free to come and go
		if (this.accountGroup.isAssociated && this.ruleSets.length === 1) {
			const alertText = `${this.accountGroup.groupName} is referenced by one or more Accounting Processes. At least one Rule Set must exist.`;
			this._promptService.alert("Cannot Delete Rule Set", alertText);
		} else {
			const sub = this._promptService.confirmMini("Confirmation", deleteTemplate).pipe(
				filter(confirmDelete => confirmDelete),
				switchMap(() => this._accountingProcessesService.deleteAccountGroupRuleSet(ruleSet.groupRuleSetId)),
				switchMap(() => this.loadRuleSets$)
			).subscribe(() => this._growlerService.success().growl("Rule Set deleted successfully"));

			this._subs.push(sub);
		}
	}
}
